import { Box } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AsyncSelectValue } from "../../../../../components/AsyncSelect";
import CustomDataGridV2 from "../../../../../components/CustomDataGridV2";
import Flex from "../../../../../components/Flex";
import RenderWithTooltip from "../../../../../components/RenderWithTooltip";
import Text from "../../../../../components/Text";
import ActionMenu from "../../../../../components/app/ActionMenu";
import CustomTitle from "../../../../../components/app/CustomTitle";
import PageLayout from "../../../../../components/PageLayout";
import { AppointmentSchedulingType } from "../../../../../dummy-data/appointment-list-data-scheduling";
import { setCallData } from "../../../../../redux/feature/join-room/JoinRoomSlice";
import { store } from "../../../../../redux/store";
import {
  formatDate,
  formatDateTime,
  formatTime,
} from "../../../../../utils/date-time-utility";
import { PatientName } from "../../../../../utils/util-components/UtilComponents";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  filterActionCol,
  getPatientVisitNoteUrl,
  replaceUnderScoreAddSpace,
  // hasActiveFilters,
} from "../../../../../utils/utils";
import { VISIT_TYPE_MAPPER } from "../../scheduling/appointment/AppointmentScheduling";
import OpenEncounterFilter from "./OpenEncounterFilter";
// import dayjs from "dayjs";
import { GridSortItem } from "@mui/x-data-grid";
import { useRoleController } from "../../../../../components/useRoleController";
import { createSessionStore } from "@/hooks/session-storage/create-session-store";
import { useAppointmentsBillingEncounter } from "../../../../../queries/use-get-appointments-billing-encounter";
import { useDebounce } from "../../../../../hooks/use-debounce";
import SearchInput from "../../../../../components/SearchInput";

export interface Filter {
  appointment_type?: AsyncSelectValue[];
  date_of_service?: string;
  location?: AsyncSelectValue[];
  providers?: AsyncSelectValue[];
  // patients?: AsyncSelectValue[];
}

// const DEFAULT_ENCOUNTER_FILTERS: Filter = {
//   appointment_type: undefined,
//   date_of_service: undefined,
//   location: undefined,
//   providers: undefined,
//   patients: undefined,
// };

// Survives a refresh but stays with this tab, so filters never leak across users.
const openEncounterFilterStore = createSessionStore<Filter | undefined>(
  "open-encounter-filter",
  undefined
);

const OpenEncounters = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [searchKey, setSearchKey] = useDebounce<string>("");
  // const [filter, setFilter] = useState<Filter>();
  const [filter, setFilter] = openEncounterFilterStore.useStore();
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { data: appointmentsList } = useAppointmentsBillingEncounter({
    pagination: pagination,
    gridSorting: gridSorting,
    appointmentType: filter?.appointment_type?.map((i: any) => i.value) as any,
    encounterDate: filter?.date_of_service ?? undefined,
    // patientUuid: filter?.patients?.map((i: any) => i.value) ?? undefined,
    providerUuid: filter?.providers?.map((i: any) => i.value) ?? undefined,
    searchString: searchKey ?? undefined,
  });
  const { hasUpdate } = useRoleController("Check in");
  const navigate = useNavigate();

  const handleStartEncounter = (appointmentData: any) => {
    if (appointmentData?.mode !== "IN_PERSON") {
      const patientIdParam = appointmentData?.patientId
        ? `&patientid=${appointmentData?.patientId}`
        : "";
      const appointmentIdParam = appointmentData?.uuid
        ? `&appointmentid=${appointmentData?.uuid}`
        : "";

      store.dispatch(
        setCallData({
          patientId: appointmentData?.patientId,
          appointmentId: appointmentData?.uuid,
          patientName: appointmentData?.patientName ?? "",
          providerName: appointmentData?.providerName ?? "",
          startTime: appointmentData?.startTime,
          role: 1,
        })
      );

      navigate(
        `/app/preview?room=${appointmentData?.room}&role=${1}&providerName=${
          appointmentData?.providerName ?? ""
        }&patientName=${appointmentData?.patientName ?? ""}&startTime=${
          appointmentData?.startTime
        }${patientIdParam}${appointmentIdParam}`
      );
    } else {
      // navigate(
      //   `${appPaths.ENCOUNTER}?patientid=${appointmentData?.patientId}&appointmentid=${appointmentData?.uuid}`
      // );
      navigate(
        getPatientVisitNoteUrl(
          appointmentData?.patientId,
          appointmentData?.uuid
        )
      );
    }
  };

  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      {
        field: "ecarehealthId",
        headerName: "Patient ID",
        flex: 0.5,
        width: 140,
        renderCell: ({ row }) => {
          return <Text isTabletext>{row?.ecarehealthId}</Text>;
        },
      },
      {
        field: "uuid",
        headerName: "Service ID",
        flex: 0.5,
        width: 160,
        renderCell: ({ row }) => {
          return (
            <Text isTabletext>{row?.uuid?.split("-")?.[0]?.toUpperCase()}</Text>
          );
        },
      },
      {
        field: "dateOfService",
        headerName: "Date Of Service",
        flex: 0.5,
        width: 160,
        renderCell: ({ row }) => {
          return (
            <Flex flexDirection={"column"}>
              <Text sx={{ lineHeight: 1.2 }} isTabletext>
                {formatDate(row.startTime)}
              </Text>
              <Text sx={{ lineHeight: 1.2 }} isTabletext>
                {formatTime(row.startTime)}
              </Text>
            </Flex>
          );
        },
      },
      {
        field: "mode",
        flex: 0.5,
        minWidth: 100,
        headerName: "Visit Type",
        sortable: false,
        renderCell: ({ row }) =>
          VISIT_TYPE_MAPPER[row.mode as AppointmentSchedulingType["mode"]],
      },
      {
        field: "patientName",
        flex: 0.6,
        minWidth: 210,
        disableColumnMenu: true,
        headerName: "Patient Name",
        renderCell: ({ row }) => (
          <PatientName
            name={row?.patientName}
            gender={row?.patientGender}
            patientId={row?.patientId ?? ""}
            birthDate={row?.patientDob}
          />
        ),
      },
      {
        field: "providerName",
        headerName: "Provider",
        flex: 0.7,
        width: 170,
        renderCell: ({ value }) => {
          return (
            <Box>
              <Text isTabletext>{value}</Text>
            </Box>
          );
        },
      },
      {
        field: "type",
        flex: 0.6,
        minWidth: 150,
        headerName: "Appointment Type",
        sortable: false,
        renderCell: ({ row }) =>
          row?.type ? replaceUnderScoreAddSpace(row?.type) : "-",
      },
      {
        field: "chiefComplaint",
        flex: 0.7,
        minWidth: 140,
        headerName: "Reason for Visit",
        sortable: false,
        renderCell: ({ value }) => <RenderWithTooltip value={value} />,
      },
      {
        field: "locationName",
        flex: 0.5,
        minWidth: 150,
        headerName: "Location",
        sortable: false,
        renderCell: ({ value }) => value || "-",
      },
      {
        field: "modified",
        flex: 0.5,
        minWidth: 100,
        headerName: "Last Updated",
        sortable: false,
        renderCell: ({ value }) => (
          <RenderWithTooltip value={formatDateTime(value)} />
        ),
      },
      {
        field: "Action",
        width: 90,
        align: "center",
        headerAlign: "center",
        hideSortIcons: true,
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={[{ value: "complete", label: "Complete Visit" }]}
              onActionClick={() => handleStartEncounter(row)}
            />
          );
        },
      },
    ],
    ["dateOfService", "patientName"]
  );

  return (
    <PageLayout
      header={
        <Flex
          justifyContent={"space-between"}
          alignItems={"center"}
          height={"2.5rem"}
        >
          <CustomTitle>Open Visit</CustomTitle>
          <Flex gap={1} alignItems={"center"} justifyContent={"flex-end"}>
            <Box width={"280px"}>
              <SearchInput
                searchKey={searchKey ?? ""}
                onSearch={(v) => setSearchKey(v)}
                placeholder="Search by Patient Id, Patient Name and Visit Id"
              />
            </Box>
            <OpenEncounterFilter
              onApply={(v) => setFilter(v)}
              defaultPreFilters={filter as any}
            />
          </Flex>
        </Flex>
      }
    >
      <CustomDataGridV2
        getRowId={(row) => row?.uuid}
        rows={appointmentsList?.data?.content ?? []}
        rowCount={appointmentsList?.data?.page?.totalElements ?? 10}
        columns={filterActionCol(COLUMNS, !hasUpdate)}
        disableRowSelectionOnClick
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        onSortModelChange={setGridSorting}
        sortModel={gridSorting}
      />
    </PageLayout>
  );
};

export default OpenEncounters;
